// Surowy dźwięk dla rozpoznawania mowy: bufor kołowy, zmiana częstotliwości
// próbkowania i zapis WAV. Czysta matematyka, bez Web Audio.
//
// Powód istnienia: wcześniej telefon nagrywał MediaRecorderem kawałki po 5 s,
// rozłączne, więc serwer widział wers pocięty w połowie, a podświetlenie
// przesuwało się raz na 6-7 s. Z bufora kołowego wysyłamy co 2 s ostatnie
// 6 s, czyli okna zachodzące na siebie, a WAV 16 kHz omija przy okazji
// różnice kodeków między Safari (mp4) a resztą przeglądarek (webm).

export const CZESTOTLIWOSC_STT = 16000;

/** Zmienia częstotliwość próbkowania uśrednianiem próbek wejścia, które
 * przypadają na jedną próbkę wyjścia (prosty filtr dolnoprzepustowy). */
export function zmienProbkowanie(wejscie: Float32Array, z: number, na: number): Float32Array {
  if (z === na) return wejscie.slice();
  const stosunek = z / na;
  const dlugosc = Math.floor(wejscie.length / stosunek);
  const wyjscie = new Float32Array(dlugosc);
  for (let i = 0; i < dlugosc; i++) {
    const od = Math.floor(i * stosunek);
    const doI = Math.min(wejscie.length, Math.floor((i + 1) * stosunek));
    let suma = 0;
    for (let j = od; j < doI; j++) suma += wejscie[j]!;
    wyjscie[i] = doI > od ? suma / (doI - od) : wejscie[od] ?? 0;
  }
  return wyjscie;
}

/** Plik WAV PCM 16-bit mono. */
export function kodujWav(probki: Float32Array, czestotliwosc: number): Uint8Array<ArrayBuffer> {
  const bajty = new Uint8Array(44 + probki.length * 2);
  const v = new DataView(bajty.buffer);
  const tekst = (off: number, s: string): void => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
  tekst(0, 'RIFF'); v.setUint32(4, 36 + probki.length * 2, true); tekst(8, 'WAVE');
  tekst(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, czestotliwosc, true); v.setUint32(28, czestotliwosc * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  tekst(36, 'data'); v.setUint32(40, probki.length * 2, true);
  for (let i = 0; i < probki.length; i++) {
    const s = Math.max(-1, Math.min(1, probki[i]!));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bajty;
}

/** Średnia energia (RMS) próbek, do pomijania okien samej ciszy. */
export function glosnosc(probki: Float32Array): number {
  let suma = 0;
  for (let i = 0; i < probki.length; i++) suma += probki[i]! * probki[i]!;
  return probki.length ? Math.sqrt(suma / probki.length) : 0;
}

/** Bufor kołowy ostatnich `pojemnosc` próbek. */
export class BuforKolowy {
  private readonly dane: Float32Array;
  private zapis = 0;
  private zapelnienie = 0;

  constructor(pojemnosc: number) {
    this.dane = new Float32Array(pojemnosc);
  }

  dopisz(probki: Float32Array): void {
    for (let i = 0; i < probki.length; i++) {
      this.dane[this.zapis] = probki[i]!;
      this.zapis = (this.zapis + 1) % this.dane.length;
    }
    this.zapelnienie = Math.min(this.dane.length, this.zapelnienie + probki.length);
  }

  get dlugosc(): number {
    return this.zapelnienie;
  }

  /** Ostatnie `n` próbek w kolejności czasowej (mniej, gdy bufor niepełny). */
  ostatnie(n: number): Float32Array {
    const ile = Math.min(n, this.zapelnienie);
    const wynik = new Float32Array(ile);
    const start = (this.zapis - ile + this.dane.length) % this.dane.length;
    for (let i = 0; i < ile; i++) wynik[i] = this.dane[(start + i) % this.dane.length]!;
    return wynik;
  }
}
